import getThreadId from '@/lib/threads/getThreadId.js';
import getThreadParentId from '@/lib/threads/getThreadParentId.js';
import getThreadProjectId from '@/lib/threads/getThreadProjectId.js';

const inheritThreadProjectMetadataFromParent = ({ threads, projectMetaById, existingThreadsById }) => {
  const threadList = Array.isArray(threads) ? threads : [];
  const threadById = {};

  for (const thread of threadList) {
    const threadId = getThreadId(thread);

    if (!threadId) {
      continue;
    }

    threadById[threadId] = thread;
  }

  const projectIdByThreadId = {};

  const resolveProjectId = (threadId, visiting = new Set()) => {
    const normalizedThreadId = String(threadId || '').trim();

    if (!normalizedThreadId) {
      return null;
    }

    if (Object.prototype.hasOwnProperty.call(projectIdByThreadId, normalizedThreadId)) {
      return projectIdByThreadId[normalizedThreadId];
    }

    if (visiting.has(normalizedThreadId)) {
      return null;
    }

    const localThread = threadById[normalizedThreadId] || existingThreadsById?.[normalizedThreadId] || null;

    if (!localThread) {
      return null;
    }

    const directProjectId = getThreadProjectId(localThread);

    if (directProjectId) {
      projectIdByThreadId[normalizedThreadId] = directProjectId;
      return directProjectId;
    }

    const parentThreadId = getThreadParentId(localThread);

    if (!parentThreadId) {
      projectIdByThreadId[normalizedThreadId] = null;
      return null;
    }

    visiting.add(normalizedThreadId);
    const inheritedProjectId = resolveProjectId(parentThreadId, visiting);
    visiting.delete(normalizedThreadId);

    projectIdByThreadId[normalizedThreadId] = inheritedProjectId || null;
    return inheritedProjectId || null;
  };

  return threadList.map((thread) => {
    const threadId = getThreadId(thread);

    if (!threadId) {
      return thread;
    }

    if (getThreadProjectId(thread)) {
      return thread;
    }

    const inheritedProjectId = resolveProjectId(threadId);

    if (!inheritedProjectId) {
      return thread;
    }

    const projectMeta = projectMetaById?.[inheritedProjectId] || null;

    return {
      ...thread,
      projectId: inheritedProjectId,
      project: projectMeta
        ? {
            ...(thread?.project || {}),
            ...projectMeta,
            id: inheritedProjectId
          }
        : thread?.project || { id: inheritedProjectId }
    };
  });
};

export default inheritThreadProjectMetadataFromParent;
